import React, { useState } from 'react';
import { css } from '@emotion/react';
import { Grid, TextField, MenuItem, InputAdornment, ListSubheader } from '@material-ui/core';
import { H1, Body1 } from '@/components/admin/AdminPricing/blocks';
import { MARKETPLACE_TIERS } from '@onward-delivery/core';
import { colors } from '@/styles';

export const COLUMNS = [
    {
        Header: 'Data',
        id: 'data-columns',
        columns: [
            {
                Header: 'Name',
                id: 'name',
                Cell: ({ row, callbacks }) => {
                    const disabled =
                        row.original.name !== 'Marketplace' &&
                        row.original.marketplace_tier !== MARKETPLACE_TIERS.T1_INTEGRATIONS_ONLY;
                    return (
                        <Body1
                            css={css`
                                ${disabled && `color: ${colors.greys[6]};`}
                            `}
                        >
                            {row.original.name}
                        </Body1>
                    );
                },
            },
            {
                Header: 'Flat Margin',
                id: 'add_margin_flat',
                span: false,
                Cell: ({ row, callbacks }) => {
                    const [temp, setTemp] = useState(row.original.add_margin_flat);
                    let value = temp !== null && temp !== undefined ? temp : row.original.add_margin_rate;
                    if (row.original.name === 'Marketplace') {
                        return <Body1>{'N/A'}</Body1>;
                    }

                    if (row.original.marketplace_tier !== MARKETPLACE_TIERS.T1_INTEGRATIONS_ONLY) {
                        return null;
                    }

                    return (
                        <Grid
                            container
                            direction="row"
                            css={css`
                                height: 100%;
                                align-items: center;
                                flex-wrap: nowrap;
                            `}
                        >
                            <TextField
                                disabled={row.original.read_only}
                                variant="outlined"
                                size="small"
                                value={value}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">$</InputAdornment>,
                                }}
                                onChange={(e) => {
                                    setTemp(e.target.value !== '' ? e.target.value : 0);
                                }}
                                onBlur={(e) => {
                                    callbacks.setOverride({
                                        name: row.original.id,
                                        attr: 'add_margin_flat',
                                        value: parseFloat(e.target.value),
                                    });
                                }}
                                fullWidth
                            />
                        </Grid>
                    );
                },
            },
            {
                Header: 'Margin Rate',
                id: 'add_margin_rate',
                span: false,
                Cell: ({ row, callbacks }) => {
                    const [temp, setTemp] = useState(row.original.add_margin_rate);
                    let value = (temp !== null && temp !== undefined ? temp : row.original.add_margin_rate) * 100;
                    if (row.original.name === 'Marketplace') {
                        return <Body1>{'N/A'}</Body1>;
                    }

                    if (row.original.marketplace_tier !== MARKETPLACE_TIERS.T1_INTEGRATIONS_ONLY) {
                        return null;
                    }

                    return (
                        <Grid
                            container
                            direction="row"
                            css={css`
                                height: 100%;
                                align-items: center;
                                flex-wrap: nowrap;
                            `}
                        >
                            <TextField
                                disabled={row.original.read_only}
                                variant="outlined"
                                size="small"
                                value={value}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                                onChange={(e) => {
                                    setTemp(e.target.value !== '' ? parseFloat(e.target.value) / 100 : 0);
                                }}
                                onBlur={(e) => {
                                    callbacks.setOverride({
                                        name: row.original.id,
                                        attr: 'add_margin_rate',
                                        value: parseFloat(e.target.value) / 100,
                                    });
                                }}
                                fullWidth
                            />
                        </Grid>
                    );
                },
            },
            {
                Header: 'Zip Filter',
                id: 'zip_geofence',
                span: false,
                Cell: ({ row, callbacks }) => {
                    if (row.original.name === 'Marketplace') {
                        return <Body1>{'N/A'}</Body1>;
                    }

                    if (row.original.marketplace_tier !== MARKETPLACE_TIERS.T1_INTEGRATIONS_ONLY) {
                        return null;
                    }

                    return (
                        <Grid
                            container
                            direction="row"
                            css={css`
                                height: 100%;
                                align-items: center;
                                flex-wrap: nowrap;
                            `}
                        >
                            <TextField
                                disabled={row.original.read_only}
                                select
                                variant="outlined"
                                size="small"
                                value={row.original.zip_geofence}
                                onChange={(e) => {
                                    callbacks.setOverride({
                                        name: row.original.id,
                                        attr: 'zip_geofence',
                                        value: e.target.value,
                                    });
                                }}
                                fullWidth
                            >
                                <MenuItem key={0} value={true}>
                                    <Body1>Yes</Body1>
                                </MenuItem>
                                <MenuItem key={1} value={false}>
                                    <Body1>No</Body1>
                                </MenuItem>
                            </TextField>
                        </Grid>
                    );
                },
            },
            {
                Header: 'Tariff',
                id: 'tariff_id',
                span: false,
                Cell: ({ row, callbacks }) => {
                    if (
                        row.original.name !== 'Marketplace' &&
                        row.original.marketplace_tier !== MARKETPLACE_TIERS.T1_INTEGRATIONS_ONLY
                    ) {
                        return null;
                    }

                    return (
                        <Grid
                            container
                            direction="row"
                            css={css`
                                height: 100%;
                                align-items: center;
                                flex-wrap: nowrap;
                            `}
                        >
                            <TextField
                                select
                                error={row.original.name === 'Marketplace' && !row.original.tariff_id}
                                disabled={row?.original?.client_tariff_options?.length === 0}
                                variant="outlined"
                                size="small"
                                value={row.original.tariff_id || ''}
                                onChange={(e) => {
                                    callbacks.setOverride({
                                        name: row.original.id,
                                        attr: 'tariff_id',
                                        value: e.target.value,
                                    });

                                    callbacks.setOverride({
                                        name: row.original.id,
                                        attr: 'marketplace_tier',
                                        value:
                                            row.original.tariff_id !== e.target.value
                                                ? MARKETPLACE_TIERS.T1_INTEGRATIONS_ONLY
                                                : row.original.marketplace_tier,
                                    });

                                    callbacks.setOverride({
                                        name: row.original.id,
                                        attr: 'carrier_tariff_id',
                                        value: e.target.value,
                                    });
                                }}
                                fullWidth
                            >
                                {[
                                    {
                                        label: `Admin Tariffs (${row?.original?.algo_type})`,
                                        attr: 'admin_tariff_options',
                                    },
                                    {
                                        label: `Carrier Tariffs (${row?.original?.algo_type})`,
                                        attr: 'client_tariff_options',
                                    },
                                ]
                                    .filter(({ attr }) => row?.original?.[attr]?.length > 0)
                                    .map(({ label, attr }) => [
                                        <ListSubheader>{label}</ListSubheader>,
                                        ...(row?.original?.[attr] || []).map(({ label, value }) => (
                                            <MenuItem
                                                key={value}
                                                value={value}
                                                disabled={attr === 'admin_tariff_options'}
                                            >
                                                <Body1>{label}</Body1>
                                            </MenuItem>
                                        )),
                                    ])}
                            </TextField>
                        </Grid>
                    );
                },
            },
        ],
    },
];
